import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Globe, Lock, Eye, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PrivacyPolicyPage() {
  const lastUpdated = "June 9, 2026";

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      {/* Header */}
      <header className="border-b border-border/50 bg-[#0A0A0B]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#00F0FF]" />
            <span className="font-semibold">Privacy Policy</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 py-12"
      >
        <div className="prose prose-invert prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: {lastUpdated}</p>

          {/* Quick Navigation */}
          <div className="bg-secondary/30 rounded-lg p-4 mb-8 not-prose">
            <p className="text-sm text-gray-400 mb-3">Quick Navigation:</p>
            <div className="flex flex-wrap gap-2">
              <a href="#data-collection" className="text-xs px-3 py-1.5 bg-[#00F0FF]/10 text-[#00F0FF] rounded-full hover:bg-[#00F0FF]/20 transition-colors flex items-center gap-1">
                <Eye className="w-3 h-3" /> Data Collection
              </a>
              <a href="#your-rights" className="text-xs px-3 py-1.5 bg-[#00F0FF]/10 text-[#00F0FF] rounded-full hover:bg-[#00F0FF]/20 transition-colors flex items-center gap-1">
                <Lock className="w-3 h-3" /> Your Rights (GDPR)
              </a>
              <a href="#data-deletion" className="text-xs px-3 py-1.5 bg-[#00F0FF]/10 text-[#00F0FF] rounded-full hover:bg-[#00F0FF]/20 transition-colors flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Data Deletion
              </a>
              <a href="#international" className="text-xs px-3 py-1.5 bg-[#00F0FF]/10 text-[#00F0FF] rounded-full hover:bg-[#00F0FF]/20 transition-colors flex items-center gap-1">
                <Globe className="w-3 h-3" /> International Transfers
              </a>
            </div>
          </div>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-gray-300 mb-4">
              iGoViral Content Creator ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
            </p>
            <p className="text-gray-300 mb-4">
              We comply with the General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other applicable data protection laws.
            </p>
            <p className="text-gray-300">
              Please read this policy carefully. By using our Service, you consent to the practices described in this Privacy Policy.
            </p>
          </section>

          <section className="mb-8" id="data-collection">
            <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-white mb-3 mt-6">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
              <li><strong>Profile Information:</strong> Profile picture, preferences, settings</li>
              <li><strong>Content:</strong> Videos, scripts, captions, and other content you create</li>
              <li><strong>Payment Information:</strong> Processed securely by Stripe (we do not store card details)</li>
              <li><strong>Communications:</strong> Messages, feedback, and support requests</li>
            </ul>

            <h3 className="text-lg font-medium text-white mb-3 mt-6">2.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Device Information:</strong> Device type, operating system, browser type</li>
              <li><strong>Usage Data:</strong> Features used, content created, interactions</li>
              <li><strong>Log Data:</strong> IP address, access times, pages viewed</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and preferences (see Section 9)</li>
            </ul>

            <h3 className="text-lg font-medium text-white mb-3 mt-6">2.3 Information from Third Parties</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Google OAuth:</strong> Name, email, profile picture (with your consent)</li>
              <li><strong>Social Media:</strong> Public profile information when you connect accounts</li>
              <li><strong>Referrals:</strong> Information about who referred you to our service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. Legal Basis for Processing (GDPR)</h2>
            <p className="text-gray-300 mb-4">
              Under the General Data Protection Regulation (GDPR), we process your data based on the following legal grounds:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Contract Performance:</strong> To provide the services you've signed up for</li>
              <li><strong>Legitimate Interests:</strong> To improve our services, prevent fraud, and ensure security</li>
              <li><strong>Consent:</strong> For marketing communications and optional analytics</li>
              <li><strong>Legal Obligations:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. How We Use Your Information</h2>
            <p className="text-gray-300 mb-4">We use the collected information to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide, maintain, and improve our Service</li>
              <li>Create and manage your account</li>
              <li>Process payments and subscriptions</li>
              <li>Process and store your video content</li>
              <li>Generate AI-powered scripts and recommendations</li>
              <li>Analyze trends and usage patterns</li>
              <li>Send service-related communications</li>
              <li>Respond to your requests and support needs</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. AI and Content Processing</h2>
            <p className="text-gray-300 mb-4">
              Our Service uses AI technologies to enhance your content creation experience. When you use AI features:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Your prompts and scripts may be processed by third-party AI providers (OpenAI, Google Gemini, fal.ai)</li>
              <li>Generated content is stored in your account for your use</li>
              <li>We do not use your content to train AI models without explicit consent</li>
              <li>AI providers may process data according to their own privacy policies</li>
              <li>Voice clones created through ElevenLabs are linked to your account only</li>
            </ul>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4">
              <p className="text-blue-200 text-sm">
                <strong>Note:</strong> AI-generated content may occasionally produce unexpected results. 
                You are responsible for reviewing all content before publishing.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Information Sharing</h2>
            <p className="text-gray-300 mb-4">We may share your information with:</p>
            
            <h3 className="text-lg font-medium text-white mb-3 mt-6">6.1 Service Providers</h3>
            <p className="text-gray-300 mb-4">
              Third-party companies that help us operate our Service, including:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Cloud hosting:</strong> IONOS (data storage)</li>
              <li><strong>AI services:</strong> OpenAI, Google Gemini, fal.ai (content generation)</li>
              <li><strong>Voice synthesis:</strong> ElevenLabs (voiceover generation)</li>
              <li><strong>Payments:</strong> Stripe (payment processing)</li>
              <li><strong>Analytics:</strong> Usage analysis (anonymized)</li>
            </ul>

            <h3 className="text-lg font-medium text-white mb-3 mt-6">6.2 Legal Requirements</h3>
            <p className="text-gray-300">
              We may disclose information if required by law, court order, or government request, or to protect our rights, safety, or property.
            </p>

            <h3 className="text-lg font-medium text-white mb-3 mt-6">6.3 Business Transfers</h3>
            <p className="text-gray-300">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">7. Data Security</h2>
            <p className="text-gray-300 mb-4">
              We implement appropriate security measures to protect your information, including:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>JWT-based authentication tokens</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
              <li>Secure payment processing via Stripe (PCI DSS compliant)</li>
            </ul>
            <p className="text-gray-300 mt-4">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Data Retention</h2>
            <p className="text-gray-300 mb-4">
              We retain your information for as long as your account is active or as needed to provide services. Specifically:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Account Data:</strong> Retained until account deletion</li>
              <li><strong>Video Content:</strong> Retained until you delete it or your account</li>
              <li><strong>Usage Logs:</strong> Retained for up to 90 days</li>
              <li><strong>Backup Data:</strong> May be retained for up to 30 days after deletion</li>
              <li><strong>Payment Records:</strong> Retained as required by law (typically 7 years)</li>
            </ul>
          </section>

          <section className="mb-8" id="your-rights">
            <h2 className="text-xl font-semibold text-white mb-4">9. Your Rights (GDPR & CCPA)</h2>
            <p className="text-gray-300 mb-4">
              Depending on your location, you have the following rights regarding your personal data:
            </p>
            
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
              <p className="text-green-200 font-medium mb-2">GDPR Rights (EU/EEA Residents)</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 text-sm">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your data ("Right to be Forgotten")</li>
                <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Restriction:</strong> Request limitation of processing</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
            </div>

            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
              <p className="text-purple-200 font-medium mb-2">CCPA Rights (California Residents)</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 text-sm">
                <li><strong>Know:</strong> Know what personal information is collected</li>
                <li><strong>Delete:</strong> Request deletion of personal information</li>
                <li><strong>Opt-Out:</strong> Opt-out of the sale of personal information</li>
                <li><strong>Non-Discrimination:</strong> Not be discriminated against for exercising rights</li>
              </ul>
            </div>

            <p className="text-gray-300">
              To exercise these rights, contact us at <span className="text-[#00F0FF]">privacy@igoviralcreator.io</span>. 
              We will respond within 30 days (GDPR) or 45 days (CCPA).
            </p>
          </section>

          <section className="mb-8" id="data-deletion">
            <h2 className="text-xl font-semibold text-white mb-4">10. Account Deletion</h2>
            <p className="text-gray-300 mb-4">
              You can delete your account and all associated data at any time:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Go to Settings → Danger Zone → Delete Account</li>
              <li>Type "DELETE" to confirm</li>
              <li>All your data will be permanently removed within 30 days</li>
              <li>This action cannot be undone</li>
            </ul>
            <p className="text-gray-300 mt-4">
              Alternatively, email us at <span className="text-[#00F0FF]">privacy@igoviralcreator.io</span> with your account email to request deletion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">11. Cookies and Tracking</h2>
            <p className="text-gray-300 mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Necessary Cookies:</strong> Required for authentication and core functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand usage patterns (optional)</li>
              <li><strong>Marketing Cookies:</strong> Used for personalized content (optional)</li>
            </ul>
            <p className="text-gray-300 mt-4">
              You can manage your cookie preferences through the cookie consent banner or your browser settings. 
              Disabling necessary cookies may affect Service functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">12. Children's Privacy</h2>
            <p className="text-gray-300">
              Our Service is not intended for children under 13 years of age (or 16 in the EU). 
              We do not knowingly collect personal information from children. 
              If you believe we have collected information from a child, please contact us immediately at{" "}
              <span className="text-[#00F0FF]">privacy@igoviralcreator.io</span>.
            </p>
          </section>

          <section className="mb-8" id="international">
            <h2 className="text-xl font-semibold text-white mb-4">13. International Data Transfers</h2>
            <p className="text-gray-300 mb-4">
              Your information may be transferred to and processed in countries other than your country of residence, including the United States and European Union.
            </p>
            <p className="text-gray-300 mb-4">
              For transfers from the EU/EEA, we rely on:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
              <li>Adequacy decisions where applicable</li>
              <li>Your explicit consent when required</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">14. Changes to This Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of material changes by:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mt-2">
              <li>Posting the new policy on this page</li>
              <li>Updating the "Last updated" date</li>
              <li>Sending you an email notification for significant changes</li>
            </ul>
            <p className="text-gray-300 mt-4">
              Your continued use of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">15. Contact Us</h2>
            <p className="text-gray-300">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 space-y-2">
              <p className="text-gray-300"><strong>Privacy Inquiries:</strong> <span className="text-[#00F0FF]">privacy@igoviralcreator.io</span></p>
              <p className="text-gray-300"><strong>Data Protection Officer:</strong> <span className="text-[#00F0FF]">dpo@igoviralcreator.io</span></p>
              <p className="text-gray-300"><strong>General Support:</strong> <span className="text-[#00F0FF]">support@igoviralcreator.io</span></p>
              <p className="text-gray-300 mt-2">Website: <a href="https://igoviralcreator.io" className="text-[#00F0FF] hover:underline">igoviralcreator.io</a></p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">16. Supervisory Authority</h2>
            <p className="text-gray-300">
              If you are in the EU/EEA and believe we have not adequately addressed your concerns, 
              you have the right to lodge a complaint with your local data protection supervisory authority.
            </p>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-border/50 flex flex-col items-center gap-4">
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} iGoViral Content Creator. All rights reserved.
          </p>
        </div>
      </motion.main>
    </div>
  );
}
